import React, { useState, useEffect, useRef } from 'react';
import { coins } from '../utils/api';

const LocationSelect = ({ value, onChange, className }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Update searchTerm when value prop changes
  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const componentRef = useRef(null);

  useEffect(() => {
    if (isOpen && componentRef.current) {
      const rect = componentRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 300; // Approximate max height of dropdown

      setDropdownPosition(spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'top' : 'bottom');
    }
  }, [isOpen]);

  // Fetch locations when search term changes
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading(true);
        const data = await coins.getStorageLocations(searchTerm);
        setLocations(data);
        setError(null);
        
        // Close dropdown if no locations found after loading
        if (data.length === 0) {
          setIsOpen(false);
        }
      } catch (err) {
        setError('Failed to load locations');
        console.error('Failed to fetch locations:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchLocations();
    }
  }, [searchTerm, isOpen]);

  return (
    <div className={`relative ${className}`} ref={componentRef}>
      <div 
        className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left border border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-300 sm:text-sm"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (isOpen && locations.length > 0) {
            switch (e.key) {
              case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => 
                  prev < locations.length - 1 ? prev + 1 : prev
                );
                break;
              case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => 
                  prev > 0 ? prev - 1 : prev
                );
                break;
              case 'Enter':
                if (isOpen && locations.length > 0) {
                  e.preventDefault();
                  const selectedLocation = locations[highlightedIndex];
                  onChange({
                    target: {
                      name: 'storage_location',
                      value: selectedLocation.location
                    }
                  });
                  setSearchTerm(selectedLocation.location);
                  setIsOpen(false);
                }
                break;
              case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                break;
            }
          }
        }}
      >
        <input
          type="text"
          id="storage_location"
          name="storage_location"
          className="w-full border-none py-2 pl-3 pr-12 text-sm leading-5 text-gray-900 focus:ring-0 truncate"
          placeholder="Enter storage location..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            onChange({
              target: {
                name: 'storage_location',
                value: e.target.value
              }
            });
            setIsOpen(true);
            setHighlightedIndex(0);
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
          }}
          onBlur={(e) => {
            // Only close if clicking outside the component and there are no locations
            if (!componentRef.current?.contains(e.relatedTarget) && locations.length === 0) {
              setIsOpen(false);
            }
          }}
        />
        <div className="absolute inset-y-0 right-0 flex items-center">
          {/* Show clear button when there is text */}
          {searchTerm && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSearchTerm('');
                onChange({
                  target: {
                    name: 'storage_location',
                    value: ''
                  }
                });
              }}
              className="p-1 hover:bg-gray-100 rounded-full"
            >
              <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
              </svg>
            </button>
          )}
          <span className="pr-1">
            <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="none" stroke="currentColor">
              <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </div>
      </div>

      {isOpen && (
        <div 
          className="fixed z-50 bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm max-h-[50vh] overflow-hidden rounded-md"
          style={{
            width: componentRef.current ? componentRef.current.getBoundingClientRect().width : 'auto',
            top: componentRef.current ? componentRef.current.getBoundingClientRect().bottom + window.scrollY : 0,
            left: componentRef.current ? componentRef.current.getBoundingClientRect().left + window.scrollX : 0
          }}
        >
          <ul className="overflow-auto max-h-[calc(50vh-2.5rem)] py-1">
          {loading && (
            <li className="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-500">
              Loading locations...
            </li>
          )}
          {error && (
            <li className="relative cursor-default select-none py-2 pl-3 pr-9 text-red-500">
              {error}
            </li>
          )}
          {!loading && !error && locations.length === 0 && (
            <li className="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-500">
              No locations found
            </li>
          )}
          {!loading && !error && locations.map((location, index) => (
            <li
              key={index}
              className={`relative cursor-default select-none py-2 pl-3 pr-9 ${
                location.location === value ? 'bg-indigo-600 text-white' : 
                index === highlightedIndex ? 'bg-indigo-100 text-gray-900' :
                'text-gray-900'
              } hover:bg-indigo-600 hover:text-white`}
              onClick={() => {
                onChange({
                  target: {
                    name: 'storage_location',
                    value: location.location
                  }
                });
                setSearchTerm(location.location);
                setIsOpen(false);
              }}
            >
              <div className="flex justify-between">
                <span>{location.location}</span>
                <span className="text-sm text-gray-500">({location.count})</span>
              </div>
            </li>
          ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LocationSelect;
